import type { CatchOutcome, CatchRecord } from '@/core/records';
import {
  type FishDefinition,
  formatLength,
  formatWeight,
  priceFor,
  weightFor,
} from '@/data/fish';
import { rasterise, parseSprite } from '@/render/spriteBaker';
import { FISH_SPRITES } from '@/sprites/fishArt';

/**
 * The card shown when a fish is landed.
 *
 * Landing a fish is the payoff for a minute of waiting, so it gets a beat of
 * its own rather than a line of toast that scrolls past. Shows the animal,
 * what it measured, and — the part that makes the next cast interesting —
 * how it compares to the best of its kind.
 *
 * Dismissed by any input, and it never blocks: the game keeps running behind
 * it, because a modal in a cosy game is a small insult.
 */

const VISIBLE_MS = 4200;
/** Sprite pixels per screen pixel. */
const ZOOM = 4;

export class CatchPopup {
  private readonly root: HTMLDivElement;
  private readonly art: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly banner: HTMLDivElement;
  private readonly stats: HTMLDListElement;
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Cached canvases; a species is drawn once however often it is caught. */
  private readonly artCache = new Map<string, HTMLCanvasElement>();

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'catch';
    this.root.hidden = true;
    this.root.setAttribute('role', 'status');
    this.root.setAttribute('aria-live', 'polite');

    this.banner = document.createElement('div');
    this.banner.className = 'catch-banner';

    this.art = document.createElement('div');
    this.art.className = 'catch-art';

    this.title = document.createElement('div');
    this.title.className = 'catch-title';

    this.stats = document.createElement('dl');
    this.stats.className = 'catch-stats';

    this.root.append(this.banner, this.art, this.title, this.stats);
    container.appendChild(this.root);
  }

  show(
    fish: FishDefinition,
    inches: number,
    outcome: CatchOutcome,
    previous: CatchRecord | undefined,
  ): void {
    const lb = weightFor(fish, inches);

    this.banner.textContent =
      outcome === 'first'
        ? 'New species!'
        : outcome === 'record'
          ? 'New record!'
          : 'Nice catch';
    this.banner.className = `catch-banner is-${outcome}`;

    this.title.textContent = fish.name;
    this.renderArt(fish.id);

    // Length first: it is what the strike difficulty was based on, and what a
    // player compares between two of the same fish.
    const rows: [string, string][] = [
      ['Length', formatLength(inches)],
      ['Weight', formatWeight(lb)],
      ['Value', `${priceFor(fish, inches)}g`],
    ];
    if (outcome === 'ordinary' && previous) {
      rows.push(['Your best', formatLength(previous.bestIn)]);
    }

    this.stats.replaceChildren();
    for (const [label, value] of rows) {
      const term = document.createElement('dt');
      term.textContent = label;
      const detail = document.createElement('dd');
      detail.textContent = value;
      this.stats.append(term, detail);
    }

    this.root.hidden = false;
    this.root.classList.add('is-showing');

    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.hide();
    }, VISIBLE_MS);
  }

  get isShowing(): boolean {
    return !this.root.hidden;
  }

  hide(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.root.classList.remove('is-showing');
    this.root.hidden = true;
  }

  destroy(): void {
    this.hide();
    this.root.remove();
  }

  private renderArt(fishId: string): void {
    let canvas = this.artCache.get(fishId);

    if (!canvas) {
      const definition = FISH_SPRITES[fishId];
      if (!definition) return;
      canvas = rasterise(parseSprite(definition));
      canvas.style.width = `${canvas.width * ZOOM}px`;
      canvas.style.height = `${canvas.height * ZOOM}px`;
      canvas.style.imageRendering = 'pixelated';
      this.artCache.set(fishId, canvas);
    }

    this.art.replaceChildren(canvas);
  }
}
